import { api, unwrap, unwrapPaged } from '../apiClient';
import type { Agent, Bank, PageMeta, Route, Shop } from '@/domain/types';

type Paged<T> = Promise<{ rows: T[]; meta: PageMeta }>;

// --- Agents -----------------------------------------------------------------

export interface AgentListParams {
  q?: string;
  isActive?: 'true' | 'false' | 'all';
  page?: number;
  pageSize?: number;
}

export interface AgentInput {
  name: string;
  contactNumber?: string | null;
  nic?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  login?: { username: string; password: string };
}

export const agentsApi = {
  list(params: AgentListParams): Paged<Agent> {
    return unwrapPaged<Agent>(api.get('/agents', { params }));
  },
  get(id: number): Promise<Agent> {
    return unwrap<Agent>(api.get(`/agents/${id}`));
  },
  suggest(q: string): Promise<Array<{ id: number; code: string; name: string }>> {
    return unwrap(api.get('/agents/suggest', { params: { q } }));
  },
  create(input: AgentInput): Promise<Agent> {
    return unwrap<Agent>(api.post('/agents', input));
  },
  update(id: number, input: Partial<AgentInput> & { isActive?: boolean }): Promise<Agent> {
    return unwrap<Agent>(api.patch(`/agents/${id}`, input));
  },
  setLogin(id: number, username: string, password: string): Promise<Agent> {
    return unwrap<Agent>(api.put(`/agents/${id}/login`, { username, password }));
  },
};

// --- Routes -----------------------------------------------------------------

export interface RouteListParams {
  q?: string;
  isActive?: 'true' | 'false' | 'all';
  page?: number;
  pageSize?: number;
}

export const routesApi = {
  list(params: RouteListParams): Paged<Route> {
    return unwrapPaged<Route>(api.get('/routes', { params }));
  },
  get(id: number): Promise<Route> {
    return unwrap<Route>(api.get(`/routes/${id}`));
  },
  suggest(q: string): Promise<Array<{ id: number; name: string; agentName: string | null }>> {
    return unwrap(api.get('/routes/suggest', { params: { q } }));
  },
  create(input: { name: string; description?: string | null; agentId?: number | null }): Promise<Route> {
    return unwrap<Route>(api.post('/routes', input));
  },
  update(
    id: number,
    input: { name?: string; description?: string | null; agentId?: number | null; isActive?: boolean },
  ): Promise<Route> {
    return unwrap<Route>(api.patch(`/routes/${id}`, input));
  },

  /** Route-to-agent mapping. `null` clears the assignment. */
  assignAgent(routeId: number, agentId: number | null): Promise<Route> {
    return unwrap<Route>(api.put(`/routes/${routeId}/agent`, { agentId }));
  },

  /** Route-to-shops mapping. */
  listShops(
    routeId: number,
  ): Promise<Array<{ id: number; code: string; name: string; contactNumber: string | null }>> {
    return unwrap(api.get(`/routes/${routeId}/shops`));
  },
  assignShops(
    routeId: number,
    shopIds: number[],
  ): Promise<{ routeId: number; assigned: number; movedFromOtherRoutes: number }> {
    return unwrap(api.post(`/routes/${routeId}/shops`, { shopIds }));
  },
  removeShop(routeId: number, shopId: number): Promise<void> {
    return api.delete(`/routes/${routeId}/shops/${shopId}`).then(() => undefined);
  },
};

// --- Shops ------------------------------------------------------------------

export interface ShopListParams {
  q?: string;
  routeId?: number;
  isActive?: 'true' | 'false' | 'all';
  unassignedOnly?: boolean;
  withOutstandingOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface ShopInput {
  name: string;
  contactPerson?: string | null;
  contactNumber?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  routeId?: number | null;
}

export const shopsApi = {
  list(params: ShopListParams): Paged<Shop> {
    return unwrapPaged<Shop>(api.get('/shops', { params }));
  },
  get(id: number): Promise<Shop> {
    return unwrap<Shop>(api.get(`/shops/${id}`));
  },
  suggest(
    q: string,
    mineOnly = false,
  ): Promise<Array<{ id: number; code: string; name: string; routeName: string | null }>> {
    return unwrap(api.get('/shops/suggest', { params: { q, mineOnly } }));
  },
  create(input: ShopInput): Promise<Shop> {
    return unwrap<Shop>(api.post('/shops', input));
  },
  update(id: number, input: Partial<ShopInput> & { isActive?: boolean }): Promise<Shop> {
    return unwrap<Shop>(api.patch(`/shops/${id}`, input));
  },
  invoices(id: number, outstanding = false) {
    return unwrap<
      Array<{
        id: number;
        invoiceNo: string;
        description: string | null;
        amountCents: number;
        allocatedCents: number;
        outstandingCents: number;
        dueDate: string;
        status: string;
      }>
    >(api.get(`/shops/${id}/invoices`, { params: { outstanding } }));
  },
};

// --- Banks ------------------------------------------------------------------

export const banksApi = {
  list(includeInactive = false): Promise<Bank[]> {
    return unwrap<Bank[]>(api.get('/banks', { params: { includeInactive } }));
  },
  create(input: { name: string; shortCode: string; sortOrder?: number }): Promise<Bank> {
    return unwrap<Bank>(api.post('/banks', input));
  },
  update(
    id: number,
    input: { name?: string; shortCode?: string; sortOrder?: number; isActive?: boolean },
  ): Promise<Bank> {
    return unwrap<Bank>(api.patch(`/banks/${id}`, input));
  },
};

// --- Users ------------------------------------------------------------------

export interface UserRow {
  id: number;
  username: string;
  fullName: string;
  role: 'ADMIN' | 'FINANCE' | 'AGENT';
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  agentId: number | null;
}

export const usersApi = {
  list(params: { q?: string; role?: string; page?: number; pageSize?: number }): Paged<UserRow> {
    return unwrapPaged<UserRow>(api.get('/users', { params }));
  },
  create(input: {
    username: string;
    password: string;
    fullName: string;
    role: 'ADMIN' | 'FINANCE';
  }): Promise<UserRow> {
    return unwrap<UserRow>(api.post('/users', input));
  },
  update(
    id: number,
    input: { fullName?: string; role?: 'ADMIN' | 'FINANCE'; isActive?: boolean },
  ): Promise<UserRow> {
    return unwrap<UserRow>(api.patch(`/users/${id}`, input));
  },
  resetPassword(id: number, password: string): Promise<{ message: string }> {
    return unwrap(api.post(`/users/${id}/reset-password`, { password }));
  },
};
